import Tooltip, { useAnalysisActiveDot } from './AnalysisTooltip';
import React from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ChartData, ChartSeries } from '../types';
import NoteMarkerOverlay, { SeriesNotes } from './NoteMarkerOverlay';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { ANALYSIS_CHART_LEFT_MARGIN, ANALYSIS_LEGEND_WRAPPER_STYLE, RUNTIME_CHART_RIGHT_MARGIN, RUNTIME_SERIES_Z_INDEX } from './analysisChartLayout';

interface RuntimeChartProps {
  jsonData: ChartData;
  overlaySeries?: ChartSeries[];
  noteSeries?: SeriesNotes[];
  onNoteImageClick?: (noteId: number) => void;
}

const RuntimeChart: React.FC<RuntimeChartProps> = ({
  jsonData,
  overlaySeries,
  noteSeries,
  onNoteImageClick,
}) => {
  const activeDot = useAnalysisActiveDot(true);
  const isOverlay = !!overlaySeries && overlaySeries.length > 1;
  const series: ChartSeries[] = isOverlay
    ? overlaySeries!
    : [{ id: 'solo', label: 'Runtime', color: '#2563eb', data: jsonData }];

  if (!series[0]?.data) return null;

  const allDatesSet = new Set<string>();
  series.forEach((s) => s.data.dates.forEach((d) => allDatesSet.add(d)));
  const allDates = Array.from(allDatesSet).sort();

  const chartData = allDates.map((date) => {
    const row: Record<string, string | number | null> = { date };
    series.forEach((s) => {
      const i = s.data.dates.indexOf(date);
      row[`runtime_${s.id}`] = i >= 0 ? s.data.runtimes[i] : null;
      row[`maxTemp_${s.id}`] = i >= 0 ? s.data.temperatures?.max?.[i] ?? null : null;
      row[`minTemp_${s.id}`] = i >= 0 ? s.data.temperatures?.min?.[i] ?? null : null;
    });
    return row;
  });

  const prefix = (s: ChartSeries) => (isOverlay ? `${s.label} — ` : '');
  const csvColumns: CsvColumn<(typeof chartData)[number]>[] = [
    { header: 'Date', value: 'date' },
    ...series.flatMap((s) => [
      { header: `${prefix(s)}Daily runtime (min)`, value: `runtime_${s.id}` },
      { header: `${prefix(s)}Outdoor max temperature (°F)`, value: `maxTemp_${s.id}` },
      { header: `${prefix(s)}Outdoor min temperature (°F)`, value: `minTemp_${s.id}` },
    ]),
  ];

  // Only show temperature axis when at least one series has valid temperature data.
  const validTemps: number[] = [];
  series.forEach((s) => {
    (s.data.temperatures?.max ?? []).forEach((t) => { if (t != null) validTemps.push(t); });
    (s.data.temperatures?.min ?? []).forEach((t) => { if (t != null) validTemps.push(t); });
  });
  const hasTemperatures = validTemps.length > 0;
  const tempDomain = hasTemperatures
    ? { min: Math.floor(Math.min(...validTemps)), max: Math.ceil(Math.max(...validTemps)) }
    : null;
  const tempPadding = tempDomain ? Math.max(5, (tempDomain.max - tempDomain.min) * 0.1) : 0;

  const formatTooltip = (value: ValueType | undefined, name: NameType | undefined): [React.ReactNode, NameType] => {
    const seriesName = name ?? '';
    if (value === undefined) return ['-', seriesName];
    if (typeof seriesName === 'string' && seriesName.includes('Runtime')) {
      const minutes = typeof value === 'number' ? value.toFixed(0) : value;
      const hours = (Number(minutes) / 60).toFixed(1);
      return [`${minutes} min (${hours} hrs)`, seriesName];
    }
    if (typeof seriesName === 'string' && (seriesName.includes('Max Temp') || seriesName.includes('Min Temp'))) {
      return [`${value}°F`, seriesName];
    }
    return [value, seriesName];
  };

  return (
    <div className="w-full mt-4 mb-8">
      <div className="flex items-start justify-between gap-2 mb-8">
        <h3 className="text-lg font-semibold">Daily Runtimes and Outdoor Temperatures</h3>
        <ChartCsvButton chart="runtime" rows={chartData} columns={csvColumns} />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1" style={{ height: '300px' }}>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData} margin={{ top: 10, right: RUNTIME_CHART_RIGHT_MARGIN, left: ANALYSIS_CHART_LEFT_MARGIN, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" yAxisId="runtime" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={50}
              />
              <YAxis
                yAxisId="runtime"
                domain={[0, 'auto']}
                label={{
                  value: 'Runtime (minutes)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -35,
                  style: { textAnchor: 'middle' },
                }}
              />
              {hasTemperatures && tempDomain && (
                <YAxis
                  yAxisId="temperature"
                  orientation="right"
                  label={{
                    value: 'Temperature (°F)',
                    angle: 90,
                    position: 'insideRight',
                    offset: 75,
                    style: { textAnchor: 'middle' },
                  }}
                  domain={[tempDomain.min - tempPadding, tempDomain.max + tempPadding]}
                />
              )}
              <Tooltip formatter={formatTooltip} />
              <Legend
                verticalAlign="top"
                height={20}
                itemSorter={null}
                wrapperStyle={ANALYSIS_LEGEND_WRAPPER_STYLE}
              />
              {series.map((s) => (
                <Bar
                  key={`runtime_${s.id}`}
                  dataKey={`runtime_${s.id}`}
                  fill={s.color}
                  name={isOverlay ? `${s.label} — Runtime` : 'Daily Runtime'}
                  yAxisId="runtime"
                  zIndex={RUNTIME_SERIES_Z_INDEX}
                />
              ))}
              {hasTemperatures && series.map((s) => (
                <Line
                  key={`maxTemp_${s.id}`}
                  type="monotone"
                  dataKey={`maxTemp_${s.id}`}
                  stroke={isOverlay ? s.color : '#dc2626'}
                  strokeDasharray={isOverlay ? '4 2' : undefined}
                  name={isOverlay ? `${s.label} — Max Temp` : 'Max Temperature'}
                  yAxisId="temperature"
                  zIndex={RUNTIME_SERIES_Z_INDEX}
                  dot={false}
                  activeDot={activeDot}
                  connectNulls={true}
                />
              ))}
              {hasTemperatures && series.map((s) => (
                <Line
                  key={`minTemp_${s.id}`}
                  type="monotone"
                  dataKey={`minTemp_${s.id}`}
                  stroke={isOverlay ? s.color : '#059669'}
                  strokeDasharray={isOverlay ? '2 4' : undefined}
                  name={isOverlay ? `${s.label} — Min Temp` : 'Min Temperature'}
                  yAxisId="temperature"
                  zIndex={RUNTIME_SERIES_Z_INDEX}
                  dot={false}
                  activeDot={activeDot}
                  connectNulls={true}
                />
              ))}
              {noteSeries && noteSeries.length > 0 && onNoteImageClick && (
                <NoteMarkerOverlay seriesNotes={noteSeries} onImageClick={onNoteImageClick} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default RuntimeChart;
